import React, { useState, useEffect, useCallback, useRef } from "react";
import { Pencil, CheckCircle2, X, Loader2, Plus, ChevronDown, Check } from "lucide-react";
import { adminService } from "../services/adminService";

// ── API ↔ UI mapping ──────────────────────────────────────────────────────────
const apiToUi = (p) => ({
  id:               p.id,
  name:             p.name          ?? "",
  price:            Number(p.price) || 0,
  popular:          p.is_popular    ?? false,
  analysesPerMonth: p.analyses_per_month,
  analysesLabel:    p.analyses_per_month === -1 ? "Unlimited" : String(p.analyses_per_month ?? 0),
  active:           p.is_active     ?? true,
  deleted:          !!(p.deletedAt ?? p.deleted_at ?? p.is_deleted),
  features:         Array.isArray(p.features) ? p.features : [],
  activeUsers:      p.active_users  ?? p.activeUsers ?? 0,
  assetsLimit:      p.assets_limit  ?? 0,
  currency:         p.currency      ?? "USD",
  analyzerType:     p.analyzer_type ?? "monthly",
});

// "Unlimited" is stored as -1; an empty field means "no cap configured" (0).
const analysesToApi = (value) => {
  if (value === undefined || value === null || value === "") return 0;
  if (String(value).toLowerCase() === "unlimited") return -1;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const uiToApi = (form) => ({
  name:               form.name,
  price:              Number(form.price) || 0,
  analyses_per_month: analysesToApi(form.analysesPerMonth),
  assets_limit:       Number(form.assetsLimit) || 0,
  is_popular:         form.popular,
  features:           form.features,
  is_active:          form.active !== false,
  currency:           form.currency,
  analyzer_type:      form.analyzerType,
});

// ── Plan options ──────────────────────────────────────────────────────────────
const PLAN_OPTIONS     = ["Starter", "Professional", "Enterprise"];
const CURRENCY_OPTIONS = ["USD", "EUR", "GBP", "INR"];
const ANALYZER_OPTIONS = [
  { label: "Unlimited & Monthly", value: "unlimited_monthly" },
  { label: "Monthly Only",        value: "monthly" },
  { label: "Unlimited Only",      value: "unlimited" },
];
const ANALYSES_OPTIONS = [
  { label: "Not enforced", value: "0" },
  { label: "5",            value: "5" },
  { label: "10",           value: "10" },
  { label: "25",           value: "25" },
  { label: "30",           value: "30" },
  { label: "50",           value: "50" },
  { label: "100",          value: "100" },
  { label: "Unlimited",    value: "unlimited" },
];

// ── Styled select ─────────────────────────────────────────────────────────────
const SelectField = ({ label, required, value, onChange, options }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const getLabel = (val) => {
    const opt = options.find((o) => (typeof o === "string" ? o : o.value) === val);
    return opt ? (typeof opt === "string" ? opt : opt.label) : val;
  };

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="flex flex-col gap-1.5" ref={ref}>
      {label && (
        <label className="text-sm font-medium text-brand-text">
          {label}{required && <span className="text-danger ml-0.5">*</span>}
        </label>
      )}
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="w-full flex items-center justify-between border border-surface-border rounded-xl px-4 py-3 text-sm text-brand-text bg-white hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
        >
          <span>{getLabel(value)}</span>
          <ChevronDown className={`w-4 h-4 text-brand-muted transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div className="absolute z-50 mt-1 w-full bg-white border border-surface-border rounded-xl shadow-lg py-1 overflow-hidden">
            {options.map((o) => {
              const optVal   = typeof o === "string" ? o : o.value;
              const optLabel = typeof o === "string" ? o : o.label;
              const selected = optVal === value;
              return (
                <button
                  key={optVal}
                  type="button"
                  onClick={() => { onChange(optVal); setOpen(false); }}
                  className={`w-full flex items-center justify-between px-4 py-2.5 text-sm text-left transition-colors hover:bg-surface ${selected ? "text-primary font-medium" : "text-brand-text"}`}
                >
                  {optLabel}
                  {selected && <Check className="w-3.5 h-3.5 text-primary" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Read-only display field ──────────────────────────────────────────────────
const ReadOnlyField = ({ label, required, value }) => (
  <div className="flex flex-col gap-1.5">
    {label && (
      <label className="text-sm font-medium text-brand-text">
        {label}{required && <span className="text-danger ml-0.5">*</span>}
      </label>
    )}
    <div className="w-full border border-surface-border rounded-xl px-4 py-3 text-sm text-brand-text bg-surface/50 select-none">
      {value}
    </div>
  </div>
);

// ── Plan Card ─────────────────────────────────────────────────────────────────
const PlanCard = ({ plan, onEdit, loadingEdit }) => (
  <div
    className={`bg-white rounded-2xl flex flex-col h-full p-6 relative transition-shadow hover:shadow-md border-2 ${
      plan.popular ? "border-primary" : "border-surface-border"
    }`}
  >
    {/* Popular badge */}
    {plan.popular && (
      <div className="mb-3">
        <span className="inline-block bg-primary text-white text-xs font-semibold px-3 py-1 rounded-full">
          Popular
        </span>
      </div>
    )}

    {/* Header: name + edit */}
    <div className="flex items-start justify-between mb-1">
      <p className="text-xl font-bold text-brand-text">{plan.name}</p>
      <button
        onClick={() => onEdit(plan)}
        disabled={loadingEdit}
        className="text-orange-400 hover:text-orange-500 transition-colors disabled:opacity-50 mt-0.5"
        title="Edit plan"
      >
        {loadingEdit
          ? <Loader2 className="w-4 h-4 animate-spin" />
          : <Pencil className="w-4 h-4" />}
      </button>
    </div>

    {/* Price */}
    <p className="text-3xl font-bold text-brand-text mb-4">
      ${plan.price}
      <span className="text-sm font-normal text-brand-muted">/mo</span>
    </p>

    {/* Stats */}
    <div className="border border-surface-border rounded-xl px-4 py-3 mb-4 space-y-2">
      <div className="flex justify-between text-sm">
        <span className="text-brand-muted">Assests/month</span>
        <span className="font-bold text-brand-text">
          {plan.assetsLimit}
        </span>
      </div>
      <div className="flex justify-between text-sm">
        <span className="text-brand-muted">Active users</span>
        <span className="font-bold text-brand-text">{plan.activeUsers}</span>
      </div>
    </div>

    {/* Features */}
    <ul className="space-y-2 flex-1">
      {plan.features.map((f) => (
        <li key={f} className="flex items-center gap-2 text-sm text-brand-text">
          <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
          {f}
        </li>
      ))}
    </ul>
  </div>
);

// ── Edit Modal ────────────────────────────────────────────────────────────────
const EditPlanModal = ({ plan, onClose, onSave, saving, error }) => {
  const [form, setForm] = useState({
    name:         plan?.name         ?? PLAN_OPTIONS[0],
    currency:     plan?.currency     ?? "USD",
    price:        plan?.price        ?? 0,
    analyzerType: plan?.analyzerType ?? ANALYZER_OPTIONS[0].value,
    assetsLimit:  String(plan?.assetsLimit ?? "0"),
    analysesPerMonth: plan?.analysesPerMonth === -1 ? "unlimited" : String(plan?.analysesPerMonth ?? "0"),
    popular:      plan?.popular      ?? false,
    active:       plan?.active       ?? true,
    features:     plan?.features     ? [...plan.features] : [],
  });
  const [featureInput, setFeatureInput] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addFeature = () => {
    const val = featureInput.trim();
    if (!val || form.features.includes(val)) return;
    set("features", [...form.features, val]);
    setFeatureInput("");
  };
  const removeFeature = (idx) => set("features", form.features.filter((_, i) => i !== idx));
  const handleKeyDown = (e) => { if (e.key === "Enter") { e.preventDefault(); addFeature(); } };

  const handleSave = () => {
    const pending = featureInput.trim();
    const allFeatures = pending && !form.features.includes(pending)
      ? [...form.features, pending]
      : form.features;
    onSave({ ...form, features: allFeatures });
  };

  const inputCls =
    "w-full border border-surface-border rounded-xl px-4 py-3 text-sm text-brand-text bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-primary">
            {plan ? "Edit Plan" : "Create New Plan"}
          </h2>
          <button onClick={onClose} disabled={saving} className="text-brand-muted hover:text-brand-text transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 px-4 py-2.5 bg-red-50 border border-red-100 text-sm text-red-600 rounded-lg">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-4">
          {/* Select Plan */}
          <ReadOnlyField
            label="Select Plan"
            required
            value={form.name}
          />

          {/* Currency + Price */}
          <div className="grid grid-cols-2 gap-3">
            <ReadOnlyField
              label="Currency"
              required
              value={form.currency}
            />
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-brand-text">
                Price<span className="text-danger ml-0.5">*</span>
              </label>
              <input
                type="number"
                min="0"
                placeholder="Price"
                value={form.price}
                onChange={(e) => set("price", e.target.value)}
                className={inputCls}
              />
            </div>
          </div>

          {/* Analyzer Type + Monthly Analyses */}
          <div className="grid grid-cols-2 gap-3">
            <ReadOnlyField
              label="Analyzer Type"
              required
              value={ANALYZER_OPTIONS.find((o) => o.value === form.analyzerType)?.label ?? form.analyzerType}
            />
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-brand-text">
                Monthly Assets<span className="text-danger ml-0.5">*</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="e.g. 50"
                value={form.assetsLimit}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, "");
                  set("assetsLimit", val);
                }}
                className={inputCls}
              />
            </div>
          </div>

          {/* Monthly analyses — the cap enforced when someone runs an analysis */}
          <div className="grid grid-cols-2 gap-3">
            <SelectField
              label="Monthly Analyses"
              required
              value={form.analysesPerMonth}
              onChange={(v) => set("analysesPerMonth", v)}
              options={ANALYSES_OPTIONS}
            />
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-brand-text">Availability</label>
              <label className="flex items-center gap-2 text-sm text-brand-text cursor-pointer select-none h-[42px]">
                <input
                  type="checkbox"
                  checked={form.active !== false}
                  onChange={(e) => set("active", e.target.checked)}
                  className="w-4 h-4 accent-primary rounded border-surface-border"
                />
                {form.active !== false ? "Listed on pricing page" : "Hidden — coming soon"}
              </label>
            </div>
          </div>

          {/* Mark as Popular */}
          <label className="flex items-center gap-2 text-sm text-brand-text cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.popular}
              onChange={(e) => set("popular", e.target.checked)}
              className="w-4 h-4 accent-primary rounded border-surface-border"
            />
            Mark as Popular
          </label>

          {/* Features */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-bold text-brand-text">Features</label>
            <div className="flex gap-2">
              <input
                placeholder="Add feature..."
                value={featureInput}
                onChange={(e) => setFeatureInput(e.target.value)}
                onKeyDown={handleKeyDown}
                className={inputCls}
              />
              <button
                onClick={addFeature}
                disabled={!featureInput.trim()}
                className="w-10 h-10 flex items-center justify-center border border-surface-border rounded-xl text-brand-muted hover:bg-primary hover:text-white hover:border-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            {form.features.length > 0 && (
              <div className="flex flex-col gap-1.5 mt-1">
                {form.features.map((f, idx) => (
                  <div key={idx} className="flex items-center justify-between px-3 py-2 bg-surface rounded-lg">
                    <div className="flex items-center gap-2 text-sm text-brand-text">
                      <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                      {f}
                    </div>
                    <button
                      onClick={() => removeFeature(idx)}
                      className="text-brand-light hover:text-danger transition-colors ml-2 flex-shrink-0"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 py-3 border border-surface-border text-sm font-semibold text-brand-text rounded-xl hover:bg-surface transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-3 bg-primary text-white text-sm font-semibold rounded-xl hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Saving…" : plan ? "Save Changes" : "Create Plan"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const AdminPricingManagementPage = () => {
  const [plans,         setPlans]         = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState("");
  const [editingPlan,   setEditingPlan]   = useState(null);
  const [modalOpen,     setModalOpen]     = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [saveErr,       setSaveErr]       = useState("");
  const [loadingEditId, setLoadingEditId] = useState(null);

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res     = await adminService.getPlans();
      const payload = res?.data ?? res;
      const list    = Array.isArray(payload) ? payload : (payload?.plans ?? payload?.data ?? []);
      setPlans(list.map(apiToUi));
    } catch (err) {
      setError(err.message || "Failed to load plans.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const handleEdit = async (plan) => {
    // Try to fetch fresh data; fall back to local plan object
    setLoadingEditId(plan.id);
    setSaveErr("");
    try {
      const res     = await adminService.getPlan(plan.id);
      const payload = res?.data?.plan ?? res?.data ?? res;
      setEditingPlan(apiToUi(payload));
    } catch {
      setEditingPlan(plan);
    } finally {
      setLoadingEditId(null);
      setModalOpen(true);
    }
  };

  const handleSave = async (form) => {
    setSaving(true);
    setSaveErr("");
    try {
      await adminService.updatePlan(editingPlan.id, uiToApi(form));
      await fetchPlans();
      setModalOpen(false);
      setEditingPlan(null);
    } catch (err) {
      setSaveErr(err.message || "Failed to save plan.");
    } finally {
      setSaving(false);
    }
  };

  const activePlans = plans.filter((p) => !p.deleted);

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-brand-text">Pricing Management</h1>
        <p className="text-sm text-brand-muted mt-0.5">
          Configure plans, features, and pricing limits.
        </p>
      </div>

      {error && (
        <div className="px-4 py-3 bg-red-50 border border-red-100 text-sm text-red-600 rounded-xl">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 text-primary animate-spin" />
        </div>
      ) : activePlans.length === 0 ? (
        <p className="text-sm text-brand-muted text-center py-10">No plans found.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 items-stretch">
          {activePlans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              onEdit={handleEdit}
              loadingEdit={loadingEditId === plan.id}
            />
          ))}
        </div>
      )}

      {modalOpen && (
        <EditPlanModal
          key={editingPlan?.id}
          plan={editingPlan}
          onClose={() => { if (!saving) { setModalOpen(false); setEditingPlan(null); } }}
          onSave={handleSave}
          saving={saving}
          error={saveErr}
        />
      )}
    </div>
  );
};

export default AdminPricingManagementPage;
